<script setup lang="ts">
import { UseMouseInElement } from "@vueuse/components";
import {
  SelectionType,
  SelfAssessmentStep,
} from "~/classes/models/self-assessment/self-assessment-step.model";

type DuplicateAttributeReviewItem = {
  step: SelfAssessmentStep;
  attribute: string;
  instanceId: string;
  label: string;
};

const props = defineProps({
  rect: {
    type: Object as PropType<{
      x: number;
      y: number;
      w: number;
      h: number;
      text: string;
    }>,
    required: true,
  },

  quillRange: {
    type: Object as PropType<{
      start: number;
      end: number;
    }>,
    required: true,
  },
  selectionType: {
    type: String as PropType<SelectionType>,
    required: true,
  },
  // Type selected or highlighted
  type: {
    type: String as PropType<"selected" | "highlighted">,
    default: "highlighted",
  },
  quillEditorSelector: {
    type: String as PropType<string>,
    required: true,
  },
  documentSelector: {
    type: String as PropType<string>,
    required: true,
  },
  index: {
    type: Number as PropType<number>,
    required: true,
  },
  duplicateAttributeItems: {
    type: Array as PropType<DuplicateAttributeReviewItem[]>,
    default: () => [],
  },
});

const emit = defineEmits(["click", "remove", "removeDuplicateAttribute"]);

const clickableBuittonDimensions = ref(12);

const padding = ref(2);

const buttonWidth = ref(12);
const buttonHeight = ref(16);

const showButton = ref(false);
const isBouncingOut = ref(false);
const hoverTimer = ref<NodeJS.Timeout | null>(null);
const lastMousePosition = ref<{ x: number; y: number } | null>(null);
const hoverDelayMs = ref(200);

const shouldShowButton = computed(() => {
  return showButton.value;
});

const handleClick = () => {
  emit("click");
};

const buttonLeft = computed(() => {
  if (props.index == 0) return 0 - buttonWidth.value + padding.value;

  return props.rect.w + padding.value * 2 - padding.value;
});

const handleRemove = (e: MouseEvent) => {
  e.stopPropagation();
  emit("remove");
};

const hasDuplicateAttributeItems = computed(() => {
  return props.duplicateAttributeItems.length > 1;
});

const isDuplicatePopoverOpen = ref(false);

const removeDuplicateAttribute = (item: DuplicateAttributeReviewItem) => {
  isDuplicatePopoverOpen.value = false;
  emit("removeDuplicateAttribute", item);
};
</script>

<template>
  <UseMouseInElement
    v-slot="{ isOutside }"
    class="absolute z-[999998] flex flex-col items-center justify-center transition-all duration-200 cursor-pointer pointer-events-none border-danger"
    :style="{
      left: rect.x - padding + 'px',
      top: rect.y - padding + 'px',
      height: rect.h + padding * 2 + 'px',
      width: rect.w + padding * 2 + 'px',
    }"
  >
    <!-- <span class="text-success-on">
      {{ rect.text }}
    </span> -->

    <!-- Remove button (only shown for first rect on hover) -->
    <Transition name="fade">
      <div v-if="!isOutside || isDuplicatePopoverOpen">
        <BasePopover
          v-if="hasDuplicateAttributeItems"
          align="start"
          side="bottom"
          trigger-class="absolute -top-1.5 -left-1.5 z-[999999]"
          @update:open="isDuplicatePopoverOpen = $event"
        >
          <button
            class="bg-surface rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] ring-[1.5px] ring-[rgba(43,80,118,0.2)] w-4 h-4 flex items-center justify-center pointer-events-auto transition-all duration-200 hover:scale-110 active:scale-95 hover:bg-slate-50 hover:text-slate-800"
            title="View duplicate attributes"
          >
            <BaseIconChevronCircleDown class="text-[12px]" />
          </button>
          <template #content>
            <div class="p-1.5 flex min-w-44 flex-col gap-0.5">
              <div class="text-xs font-semibold text-muted-foreground px-2 py-1">
                Remove Duplicate
              </div>
              <button
                v-for="item in duplicateAttributeItems"
                :key="`${item.attribute}-${item.instanceId}`"
                class="flex h-auto w-full flex-row items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-xs font-normal hover:bg-accent hover:text-accent-foreground"
                @click.stop.prevent="removeDuplicateAttribute(item)"
              >
                <span class="min-w-0 truncate">
                  {{ item.label }}
                </span>
                <BaseIconClose class="shrink-0 text-[9px] text-danger-default" />
              </button>
            </div>
          </template>
        </BasePopover>

        <button
          v-else
          @click="$emit('click')"
          class="remove-button absolute -top-1.5 -left-1.5 w-4 h-4 flex items-center justify-center pointer-events-auto transition-all duration-200 hover:scale-110 active:scale-95 z-[999999]"
          title="Remove highlight"
        >
          <BaseIconClose class="text-[8px]" />
        </button>
      </div>
    </Transition>
  </UseMouseInElement>
</template>

<style scoped>
.selected-rect {
  font-family: "Times New Roman", serif !important;
  @apply text-white;
  background: #4a7db5;
  border-radius: 4px;
  box-shadow:
    0 2px 4px rgba(74, 125, 181, 0.3),
    0 0 0 1px rgba(74, 125, 181, 0.4);
}

.remove-button {
  background: white;
  border-radius: 50%;
  color: #475569;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.15),
    0 0 0 1.5px rgba(43, 80, 118, 0.2);
}

.remove-button:hover {
  background: #f8fafc;
  color: #1e293b;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.2),
    0 0 0 2px rgba(43, 80, 118, 0.3);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
